import { TransitionDirection } from '@atlas/types'
import type { WorkflowStatusDTO, WorkflowTransitionDTO } from '@/domain/workflow-admin.types'

export interface BuilderNode {
	id: string
	code: string
	label: string
	phase: WorkflowStatusDTO['phase']
	workspaceDefault: WorkflowStatusDTO['workspaceDefault']
	isInitial: boolean
	isTerminal: boolean
	/** Determines which direction a freshly-drawn connector defaults to — see `pairKey`'s caller in `WorkflowBuilderPage.tsx`. */
	position: number
	x: number
	y: number
}

/** One direction of a connector — a single real `workflow_transition` row. */
export interface BuilderDirection {
	transitionId: string
	ctaLabel: string
}

/** A connector between two nodes — up to two real transition rows, one per direction. */
export interface BuilderEdge {
	/** Stable key independent of which direction(s) exist, so React keys survive adding/removing a direction. */
	key: string
	fromId: string
	toId: string
	forward: BuilderDirection | null
	backward: BuilderDirection | null
}

export const NODE_WIDTH = 190
export const NODE_HEIGHT = 70
const COLUMNS = 4
const COL_GAP = 250
const ROW_GAP = 160
const MARGIN = 40

/** The canvas's dot-grid spacing — a dragged node snaps to this, and the canvas's own dotted background uses the same value so the anchor points are the dots. */
export const GRID_SIZE = 22

export const snapToGrid = (value: number): number => Math.round(value / GRID_SIZE) * GRID_SIZE

/**
 * A status with a saved `canvasX`/`canvasY` (dragged at least once) renders
 * at that persisted position; one that's never been dragged falls back to a
 * deterministic grid slot by `position` order (never a real graph-layout
 * library — there's nothing to lay out until a status has no saved position).
 */
export function layoutNodes(statuses: readonly WorkflowStatusDTO[]): BuilderNode[] {
	const ordered = statuses.slice().sort((a, b) => a.position - b.position)
	return ordered.map((s, i) => ({
		id: s.statusId,
		code: s.code,
		label: s.label,
		phase: s.phase,
		workspaceDefault: s.workspaceDefault,
		isInitial: s.isInitial,
		isTerminal: s.isTerminal,
		position: s.position,
		x: s.canvasX ?? snapToGrid(MARGIN + (i % COLUMNS) * COL_GAP),
		y: s.canvasY ?? snapToGrid(MARGIN + Math.floor(i / COLUMNS) * ROW_GAP),
	}))
}

/** Unordered pair key so A→B and B→A group onto the same connector — exported so a caller can predict a new connector's key before its first transition round-trips. */
export const pairKey = (a: string, b: string) => [a, b].sort().join('::')

/**
 * Groups every real `workflow_transition` row into connectors between two
 * nodes — up to one FORWARD row and one BACKWARD row per pair, matching the
 * builder's "two independent transitions share one connector" model.
 */
export function buildEdges(transitions: readonly WorkflowTransitionDTO[]): BuilderEdge[] {
	const byPair = new Map<string, BuilderEdge>()
	for (const t of transitions) {
		const key = pairKey(t.fromStatusId, t.toStatusId)
		const existing = byPair.get(key)
		const direction: BuilderDirection = { transitionId: t.transitionId, ctaLabel: t.ctaLabel }
		if (t.direction === TransitionDirection.BACKWARD) {
			if (existing) existing.backward = direction
			else byPair.set(key, { key, fromId: t.toStatusId, toId: t.fromStatusId, forward: null, backward: direction })
		} else if (existing) existing.forward = direction
		else byPair.set(key, { key, fromId: t.fromStatusId, toId: t.toStatusId, forward: direction, backward: null })
	}
	return [...byPair.values()]
}

/**
 * Whether a connector's stored directions still match the workflow's step
 * order, from the positions of its two endpoints in forward orientation
 * (`fromId → toId`).
 *
 * Both of a connector's rows are the same traversal read two ways, so they
 * agree or disagree together: the answer depends only on whether the
 * connector runs up the order, never on which tab is open. The transition
 * engine classifies a move as backward purely on `position`, so a connector
 * whose orientation the order contradicts is gated as the opposite of what it
 * reads as on the canvas - which is what a transition drawn before its status
 * was reordered becomes once the reorder lands.
 *
 * Two statuses sharing a position leave the comparison undefined rather than
 * wrong, so it reports as `tied`: reordering the workflow is the repair, not
 * flipping a direction.
 */
export type SequenceAgreement = 'agrees' | 'disagrees' | 'tied'

export function sequenceAgreementOf(fromPosition: number, toPosition: number): SequenceAgreement {
	if (fromPosition === toPosition) return 'tied'
	return fromPosition < toPosition ? 'agrees' : 'disagrees'
}

/** Point on a `w`×`h` rectangle centered at `(cx,cy)`, on the boundary in the direction of `(tx,ty)`. */
function clipToRect(cx: number, cy: number, w: number, h: number, tx: number, ty: number): { x: number; y: number } {
	const dx = tx - cx
	const dy = ty - cy
	if (dx === 0 && dy === 0) return { x: cx, y: cy }
	const halfW = w / 2
	const halfH = h / 2
	const scaleX = dx !== 0 ? halfW / Math.abs(dx) : Infinity
	const scaleY = dy !== 0 ? halfH / Math.abs(dy) : Infinity
	const scale = Math.min(scaleX, scaleY)
	return { x: cx + dx * scale, y: cy + dy * scale }
}

/** One direction's own clipped, colorable, clickable line segment. */
export interface DirectionLine {
	id: string
	edgeKey: string
	dir: 'forward' | 'backward'
	transitionId: string
	ctaLabel: string
	x1: number
	y1: number
	x2: number
	y2: number
}

const nodeCenter = (n: BuilderNode) => ({ cx: n.x + NODE_WIDTH / 2, cy: n.y + NODE_HEIGHT / 2 })

/**
 * How far apart the two lines of a bidirectional connector sit, so they
 * never read as one. At 2.5px stroke width, the old value of 5 left only a
 * ~2.5px visible gap between the two strokes — easy to miss at a glance,
 * especially on a near-horizontal connector where the offset is nearly all
 * vertical and the eye is scanning left-right.
 */
const PARALLEL_OFFSET = 12

/**
 * Up to two independently-colored line segments for a connector — one per
 * existing direction (color carries the direction; there is no separate
 * label/dot). Both share the same node-boundary-to-boundary clip, offset a
 * few px apart when both directions exist so neither line hides the other.
 * Forward runs source→target (arrow at the target end); backward is the
 * mirror image (arrow at the source end) — never the same segment reversed
 * in place, since the two are independent `workflow_transition` rows.
 */
export function directionLinesFor(edge: BuilderEdge, nodesById: Map<string, BuilderNode>): DirectionLine[] {
	const from = nodesById.get(edge.fromId)
	const to = nodesById.get(edge.toId)
	if (!from || !to) return []
	const a = nodeCenter(from)
	const b = nodeCenter(to)
	const p1 = clipToRect(a.cx, a.cy, NODE_WIDTH, NODE_HEIGHT, b.cx, b.cy)
	const p2 = clipToRect(b.cx, b.cy, NODE_WIDTH, NODE_HEIGHT, a.cx, a.cy)
	const dx = p2.x - p1.x
	const dy = p2.y - p1.y
	const len = Math.hypot(dx, dy) || 1
	const bothPresent = !!edge.forward && !!edge.backward
	const offset = bothPresent ? PARALLEL_OFFSET : 0
	const perpX = (-dy / len) * offset
	const perpY = (dx / len) * offset

	const lines: DirectionLine[] = []
	if (edge.forward)
		lines.push({
			id: `${edge.key}-fwd`,
			edgeKey: edge.key,
			dir: 'forward',
			transitionId: edge.forward.transitionId,
			ctaLabel: edge.forward.ctaLabel,
			x1: p1.x + perpX,
			y1: p1.y + perpY,
			x2: p2.x + perpX,
			y2: p2.y + perpY,
		})
	if (edge.backward)
		lines.push({
			id: `${edge.key}-back`,
			edgeKey: edge.key,
			dir: 'backward',
			transitionId: edge.backward.transitionId,
			ctaLabel: edge.backward.ctaLabel,
			x1: p2.x - perpX,
			y1: p2.y - perpY,
			x2: p1.x - perpX,
			y2: p1.y - perpY,
		})
	return lines
}
